import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  DEFAULT_ETA_SETTINGS,
  etaSettingsSchema,
  type BoardEtaRow,
  type EtaCallCounts,
  type EtaSettings,
} from '@rapid-response/shared'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Switch } from '~/components/ui/switch'
import { useCurrentUser } from '~/lib/current-user'
import { fmtDateTime, fmtDuration, fmtExecutionStatus, fmtMiles } from '~/lib/dispatch-format'
import { CONFIDENCE_META, fmtKestrelBoardCell } from '~/lib/eta-format'
import {
  getEtaCallCountsFn,
  getEtaSettingsFn,
  listBoardEtasFn,
  updateEtaSettingsFn,
} from '~/server/eta-settings'
import { seo } from '~/utils/seo'

export const Route = createFileRoute('/admin/eta-settings')({
  head: () => ({ meta: seo({ title: 'Admin — ETA settings' }) }),
  component: AdminEtaSettingsPage,
})

type SwitchKey = 'kestrelTrackerEnabled' | 'kestrelBoardEnabled'
type NumberKey = Exclude<keyof EtaSettings, SwitchKey>

const FIELDS: { key: NumberKey; label: string; hint: string }[] = [
  { key: 'moveThresholdMetres', label: 'Move threshold (metres)', hint: '50 to 50,000. An officer who moves further than this gets a new ETA without waiting for the refresh time.' },
  { key: 'refreshMinutes', label: 'Refresh time (minutes)', hint: '1 to 60. How old an ETA gets before it is worked out again, whether or not the officer has moved.' },
  { key: 'gpsMaxAgeMinutes', label: 'GPS age limit (minutes)', hint: '5 to 120. A latest fix older than this gives no new ETA; an existing ETA is kept but its confidence reads insufficient data.' },
  { key: 'callsPerMinute', label: 'Google calls per minute', hint: '1 to 3,000. EGIS\'s ceiling across all requests and servers; Kestrel only uses what EGIS leaves.' },
]

const SWITCHES: { key: SwitchKey; label: string; hint: string }[] = [
  { key: 'kestrelTrackerEnabled', label: 'Compare Kestrel on the ETA tracker', hint: 'Shows Kestrel\'s position, route and ETA beside EGIS\'s on the ETA tracker.' },
  { key: 'kestrelBoardEnabled', label: 'Compare Kestrel on the Argus board ETAs', hint: 'Adds a Kestrel ETA to each board ETA row for Argus, using at most half the Google calls per minute, and only what EGIS leaves.' },
]

type Draft = Record<NumberKey, string>
type Switches = Record<SwitchKey, boolean>

const toDraft = (s: EtaSettings): Draft => ({
  moveThresholdMetres: String(s.moveThresholdMetres),
  refreshMinutes: String(s.refreshMinutes),
  gpsMaxAgeMinutes: String(s.gpsMaxAgeMinutes),
  callsPerMinute: String(s.callsPerMinute),
})

const toSwitches = (s: EtaSettings): Switches => ({
  kestrelTrackerEnabled: s.kestrelTrackerEnabled,
  kestrelBoardEnabled: s.kestrelBoardEnabled,
})

/** Validation messages from the shared schema, or null when the draft is valid. */
function draftError(draft: Draft): string | null {
  const parsed = etaSettingsSchema.safeParse(
    Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, v.trim() === '' ? Number.NaN : Number(v)])),
  )
  if (parsed.success) return null
  return parsed.error.issues.map((i) => `${String(i.path[0])}: ${i.message}`).join('; ')
}

function SettingsForm({ saved, onSaved }: Readonly<{ saved: EtaSettings; onSaved: (s: EtaSettings) => void }>) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(saved))
  const [switches, setSwitches] = useState<Switches>(() => toSwitches(saved))
  const [saving, setSaving] = useState(false)
  const error = draftError(draft)

  useEffect(() => {
    setDraft(toDraft(saved))
    setSwitches(toSwitches(saved))
  }, [saved])

  async function save() {
    setSaving(true)
    try {
      const next = await updateEtaSettingsFn({ data: { input: etaSettingsSchema.parse({
        moveThresholdMetres: Number(draft.moveThresholdMetres),
        refreshMinutes: Number(draft.refreshMinutes),
        gpsMaxAgeMinutes: Number(draft.gpsMaxAgeMinutes),
        callsPerMinute: Number(draft.callsPerMinute),
        ...switches,
      }) } })
      onSaved(next)
      toast.success('ETA settings saved')
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="space-y-4 rounded-md border bg-card p-6">
      <div>
        <h2 className="text-lg font-semibold">Settings</h2>
        <p className="text-sm text-muted-foreground">
          How board ETAs for Argus are refreshed. Defaults: {FIELDS.map((f) => DEFAULT_ETA_SETTINGS[f.key]).join(', ')}.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {FIELDS.map((f) => (
          <div key={f.key} className="space-y-1">
            <Label htmlFor={f.key}>{f.label}</Label>
            <Input
              id={f.key}
              type="number"
              value={draft[f.key]}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">{f.hint}</p>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        {SWITCHES.map((f) => (
          <div key={f.key} className="flex items-start gap-3">
            <Switch
              id={f.key}
              checked={switches[f.key]}
              onCheckedChange={(checked) => setSwitches({ ...switches, [f.key]: checked })}
            />
            <div className="space-y-1">
              <Label htmlFor={f.key}>{f.label}</Label>
              <p className="text-xs text-muted-foreground">{f.hint}</p>
            </div>
          </div>
        ))}
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button onClick={save} disabled={saving || error !== null}>
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </section>
  )
}

function CallCounts({ counts }: Readonly<{ counts: EtaCallCounts | null }>) {
  const items = [
    ['Last hour', counts?.lastHour],
    ['Failed in the last hour', counts?.failedLastHour],
    ['Last 24 hours', counts?.last24Hours],
  ] as const
  return (
    <section className="space-y-3 rounded-md border bg-card p-6">
      <h2 className="text-lg font-semibold">Google calls</h2>
      <dl className="grid gap-4 sm:grid-cols-3">
        {items.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="text-2xl font-semibold">{value ?? '—'}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

const dash = (v: string | number | null) => v ?? '—'

function BoardEtaRowView({ row }: Readonly<{ row: BoardEtaRow }>) {
  const confidence = row.confidence ? CONFIDENCE_META[row.confidence] : null
  return (
    <tr className="border-t align-top">
      <td className="py-2 font-mono text-xs">{row.jobNumber}</td>
      <td className="py-2">{dash(row.executionStatus && fmtExecutionStatus(row.executionStatus))}</td>
      <td className="py-2">{dash(row.officerName)}</td>
      <td className="py-2">{dash(row.partnerName)}</td>
      <td className="py-2 text-muted-foreground">{dash(fmtDateTime(row.lastSeen))}</td>
      <td className="py-2">{dash(fmtDateTime(row.eta))}</td>
      <td className="py-2">{row.driveMinutes === null ? '—' : fmtDuration(row.driveMinutes * 60)}</td>
      <td className="py-2">{row.driveMiles === null ? '—' : fmtMiles(row.driveMiles)}</td>
      <td className="py-2 text-muted-foreground">{dash(fmtDateTime(row.etaComputedAt))}</td>
      <td className="py-2">
        {confidence ? (
          <Badge className={confidence.badgeClass}>
            {confidence.label}
            {row.confidenceScore === null ? '' : ` ${Math.round(row.confidenceScore)}`}
          </Badge>
        ) : (
          '—'
        )}
      </td>
      <td className="py-2">{dash(row.noEtaReason)}</td>
      <td className="py-2">{fmtKestrelBoardCell(row.kestrel)}</td>
      <td className="py-2 text-muted-foreground">{dash(fmtDateTime(row.checkedAt))}</td>
    </tr>
  )
}

const COLUMNS = [
  'Job', 'Status', 'Officer', 'Partner', 'Last seen', 'ETA', 'Drive', 'Miles',
  'Computed', 'Confidence', 'No ETA reason', 'Kestrel', 'Checked',
]

function BoardEtaTable({ rows }: Readonly<{ rows: BoardEtaRow[] | null }>) {
  let body = (
    <table className="w-full text-sm">
      <thead className="text-left text-xs text-muted-foreground">
        <tr>
          {COLUMNS.map((c) => (
            <th key={c} className="py-2 pr-2 font-medium">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>{rows?.map((r) => <BoardEtaRowView key={r.jobNumber} row={r} />)}</tbody>
    </table>
  )
  if (rows === null) body = <div className="text-sm text-muted-foreground">Loading board ETAs...</div>
  else if (rows.length === 0) body = <p className="text-sm italic text-muted-foreground">No board ETAs requested in the last hour.</p>
  return (
    <section className="space-y-3 rounded-md border bg-card p-6">
      <div>
        <h2 className="text-lg font-semibold">Board ETAs</h2>
        <p className="text-sm text-muted-foreground">Jobs Argus asked about in the last hour, newest first. Read from storage only.</p>
      </div>
      <div className="overflow-x-auto">{body}</div>
    </section>
  )
}

function AdminEtaSettingsPage() {
  const { user } = useCurrentUser()
  const [settings, setSettings] = useState<EtaSettings | null>(null)
  const [counts, setCounts] = useState<EtaCallCounts | null>(null)
  const [rows, setRows] = useState<BoardEtaRow[] | null>(null)

  useEffect(() => {
    if (!user) return
    Promise.all([getEtaSettingsFn(), getEtaCallCountsFn(), listBoardEtasFn()])
      .then(([s, c, r]) => {
        setSettings(s)
        setCounts(c)
        setRows(r)
      })
      .catch((e: Error) => toast.error(e.message))
  }, [user])

  return (
    <div className="space-y-6">
      {settings ? (
        <SettingsForm saved={settings} onSaved={setSettings} />
      ) : (
        <div className="text-sm text-muted-foreground">Loading settings...</div>
      )}
      <CallCounts counts={counts} />
      <BoardEtaTable rows={rows} />
    </div>
  )
}
